import { store, RootState } from "@core/store"
import { baseUrl } from "../store/advApi"
import { AdvertisingContent, ReplyMarkup } from "../interfaces/contents"

export interface ContentUploadError {
  status: number
  code?: string
  error: string
}

export const isContentUploadError = (err: unknown): err is ContentUploadError =>
  typeof err === "object" && err !== null && "status" in err && "error" in err

export interface UploadContentArgs {
  text?: string
  files: File[]
  assetIds: number[]
  replyMarkup?: ReplyMarkup
  campaignIds: number[]
  orderIds: number[]
  onProgress?: (pct: number) => void
  signal?: AbortSignal
}

const NETWORK_MESSAGE = "Нет соединения с сервером"
const ABORT_MESSAGE =
  "Отправка отменена. Результат неизвестен — обновите список"

const parseHttpError = (
  status: number,
  responseText: string,
): ContentUploadError => {
  try {
    const parsed = JSON.parse(responseText) as {
      error?: unknown
      code?: unknown
    }
    if (parsed && typeof parsed.error === "string") {
      return {
        status,
        code: typeof parsed.code === "string" ? parsed.code : undefined,
        error: parsed.error,
      }
    }
  } catch {
    // не-JSON тело (HTML прокси, XML MinIO) — fallback ниже
  }
  return { status, error: `HTTP ${status}` }
}

// XHR-загрузка multipart-ветки создания (только она; JSON-ветка идёт через
// baseQuery в queryFn мутации createContent). Content-Type НЕ ставится —
// boundary ставит браузер; токен — из стора, как в prepareHeaders advApi
// (NFR-8); авторетраев нет; таймаут не ставится (FR-37).
export const uploadContent = (
  args: UploadContentArgs,
): Promise<AdvertisingContent> =>
  new Promise((resolve, reject) => {
    const rejectAborted = (): void => {
      reject({ status: 0, code: "aborted", error: ABORT_MESSAGE })
    }
    if (args.signal?.aborted) {
      rejectAborted()
      return
    }
    const xhr = new XMLHttpRequest()
    xhr.open("POST", `${baseUrl}contents`)
    const token = (store.getState() as RootState).auth.token
    if (token) {
      xhr.setRequestHeader("Authorization", `Bearer ${token}`)
    }
    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable && args.onProgress) {
        args.onProgress(Math.round((event.loaded / event.total) * 100))
      }
    }
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        try {
          resolve(JSON.parse(xhr.responseText) as AdvertisingContent)
        } catch {
          reject({ status: xhr.status, error: `HTTP ${xhr.status}` })
        }
        return
      }
      reject(parseHttpError(xhr.status, xhr.responseText))
    }
    xhr.onerror = () => {
      reject({ status: 0, error: NETWORK_MESSAGE })
    }
    xhr.onabort = () => {
      rejectAborted()
    }
    args.signal?.addEventListener("abort", () => xhr.abort(), { once: true })
    const form = new FormData()
    if (args.text !== undefined && args.text.trim() !== "") {
      form.append("text", args.text)
    }
    for (const file of args.files) {
      form.append("files", file, file.name)
    }
    for (const assetId of args.assetIds) {
      form.append("asset_ids", String(assetId))
    }
    if (args.replyMarkup && args.replyMarkup.inline_keyboard.length > 0) {
      form.append("reply_markup", JSON.stringify(args.replyMarkup))
    }
    for (const id of args.campaignIds) {
      form.append("campaign_ids", String(id))
    }
    for (const id of args.orderIds) {
      form.append("order_ids", String(id))
    }
    xhr.send(form)
  })

// Теги инвалидации мутаций материалов (§2.5): LIST + ContentsFilters + Orders
// всегда; кампания → {type:"Order"} (все — campaign_contents её ордеров,
// набор id неизвестен, §10.5), иначе точечные {type:"Order", id} по orderIds.
export type ContentMutationTag =
  | { type: "Contents"; id: "LIST" }
  | "ContentsFilters"
  | "Orders"
  | "Order"
  | { type: "Order"; id: number }

export interface ContentMutationInvalidatesArgs {
  campaignIds?: number[]
  orderIds?: number[]
}

export const contentMutationInvalidates = (
  args: ContentMutationInvalidatesArgs,
): ContentMutationTag[] => {
  const tags: ContentMutationTag[] = [
    { type: "Contents", id: "LIST" },
    "ContentsFilters",
    "Orders",
  ]
  if (args.campaignIds && args.campaignIds.length > 0) {
    tags.push("Order")
    return tags
  }
  for (const id of args.orderIds ?? []) {
    tags.push({ type: "Order", id })
  }
  return tags
}
